import React, { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../supabase'
import { useAuth } from '../context/AuthContext'
import InvoiceModal from './InvoiceModal'
import { fmtINR, fmtInvDate, invDate, normStatus } from '../lib/billing'
import { resolvePrescriber, regLine, printPrescription, buildRxWhatsApp } from '../lib/prescription'
import { friendlyError } from '../lib/permissions'

// ─── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  teal900: '#0d3d3d', teal800: '#0f5454', teal700: '#126868',
  teal600: '#148080', teal500: '#19a8a8', teal400: '#2ecece',
  teal200: '#90e0e0', teal100: '#ccf2f2', teal50: '#edfafa',
  white: '#ffffff',
  gray50: '#f8fafc', gray100: '#f1f5f9', gray200: '#e2e8f0',
  gray300: '#cbd5e1', gray400: '#94a3b8', gray500: '#64748b',
  gray600: '#475569', gray700: '#334155', gray900: '#0f172a',
  red50: '#fff1f2', red600: '#dc2626',
  green50: '#f0fdf4', green600: '#16a34a',
  amber50: '#fffbeb', amber600: '#d97706',
  purple50: '#faf5ff', purple600: '#9333ea',
  blue50: '#eff6ff', blue600: '#2563eb',
  font: "'Segoe UI', system-ui, -apple-system, sans-serif",
}

const SKIN_TYPES   = ['Normal', 'Dry', 'Oily', 'Combination', 'Sensitive']
const GENDERS      = ['Male', 'Female', 'Other']
const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-']
const BODY_AREAS   = ['Face', 'Forehead', 'Cheek', 'Back', 'Arms', 'Scalp', 'Neck', 'Full Body', 'Other']

/* ─── One-time Supabase setup (run in SQL editor) ─────────────────────────────
CREATE TABLE IF NOT EXISTS patient_photos (
  id          UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
  patient_id  UUID NOT NULL REFERENCES patients(id) ON DELETE CASCADE,
  photo_url   TEXT NOT NULL,
  body_area   TEXT NOT NULL,
  visit_date  DATE NOT NULL DEFAULT CURRENT_DATE,
  notes       TEXT,
  created_at  TIMESTAMPTZ NOT NULL DEFAULT NOW()
);
ALTER TABLE patient_photos ENABLE ROW LEVEL SECURITY;
CREATE POLICY "auth_all_photos" ON patient_photos
  FOR ALL TO authenticated USING (true) WITH CHECK (true);
──────────────────────────────────────────────────────────────────────────── */

// ─── Avatar colour from name ───────────────────────────────────────────────────
const AVATAR_COLORS = [
  ['#0f5454','#2ecece'], ['#1e3a5f','#60a5fa'], ['#3b1f6e','#a78bfa'],
  ['#5c1a1a','#f87171'], ['#1a4731','#34d399'], ['#5c3d0e','#fbbf24'],
  ['#1f3a5c','#38bdf8'], ['#4a1942','#e879f9'],
]
function avatarColor(name = '') {
  const i = name.charCodeAt(0) % AVATAR_COLORS.length
  return AVATAR_COLORS[i]
}
function initials(name = '') {
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?'
}

// ─── Small helpers ─────────────────────────────────────────────────────────────
function fmtDate(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}
function fmtTime(t) {
  if (!t) return ''
  const [h, m] = t.split(':')
  const hr = parseInt(h, 10)
  return `${hr % 12 || 12}:${m} ${hr < 12 ? 'AM' : 'PM'}`
}
function whatsappURL(phone) {
  const num = (phone || '').replace(/\D/g, '')
  return `https://wa.me/91${num}`
}
function isoToday() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
}

// ─── Prescription pad constants ────────────────────────────────────────────────
const ADVICE_OPTIONS = [
  'Avoid sun exposure', 'Apply SPF 50+ sunscreen daily', 'Avoid spicy/oily food',
  'Keep area moisturised', 'Avoid scratching', 'Stay well hydrated',
  'Avoid alcohol & smoking', 'Gentle cleansing only',
]
const RX_FOLLOW_UP = ['1 week', '2 weeks', '1 month', '3 months', '6 months', 'As needed', 'SOS']

// ─── Shared input style (for modals) ──────────────────────────────────────────
const mSx = {
  width: '100%', padding: '9px 12px', border: `1.5px solid #e2e8f0`,
  borderRadius: 8, fontSize: 13.5, color: '#0f172a', background: '#f8fafc',
  outline: 'none', fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
  boxSizing: 'border-box',
}
function MInput({ value, onChange, placeholder, type = 'text', style }) {
  return (
    <input type={type} value={value} onChange={onChange} placeholder={placeholder}
      style={{ ...mSx, ...style }}
      onFocus={e => { e.target.style.borderColor = '#19a8a8'; e.target.style.boxShadow = '0 0 0 3px #ccf2f2' }}
      onBlur={e =>  { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none' }}
    />
  )
}
function MSelect({ value, onChange, children }) {
  return (
    <select value={value} onChange={onChange} style={{ ...mSx, cursor: 'pointer' }}
      onFocus={e => { e.target.style.borderColor = '#19a8a8'; e.target.style.boxShadow = '0 0 0 3px #ccf2f2' }}
      onBlur={e =>  { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none' }}
    >{children}</select>
  )
}
function MLabel({ children }) {
  return <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{children}</label>
}

// ─── Status badge ──────────────────────────────────────────────────────────────
const STATUS_COLORS = {
  Scheduled:  { bg: T.blue50,   color: T.blue600,   border: '#bfdbfe' },
  Confirmed:  { bg: T.teal50,   color: T.teal700,   border: T.teal200 },
  Completed:  { bg: T.green50,  color: T.green600,  border: '#bbf7d0' },
  Cancelled:  { bg: T.red50,    color: T.red600,    border: '#fecdd3' },
  'No Show':  { bg: T.amber50,  color: T.amber600,  border: '#fde68a' },
}
function Badge({ text, colors }) {
  const c = colors || { bg: T.gray100, color: T.gray600, border: T.gray200 }
  return (
    <span style={{
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
      borderRadius: 20, padding: '2px 10px', fontSize: 11.5, fontWeight: 600,
    }}>{text}</span>
  )
}

// ─── Input component ───────────────────────────────────────────────────────────
function Field({ label, children, half }) {
  return (
    <div style={{ marginBottom: 14, width: half ? 'calc(50% - 6px)' : '100%' }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: T.gray600, marginBottom: 5 }}>
        {label}
      </label>
      {children}
    </div>
  )
}
const inputSx = {
  width: '100%', padding: '9px 12px', border: `1.5px solid ${T.gray200}`,
  borderRadius: 8, fontSize: 13.5, color: T.gray900, background: T.gray50,
  outline: 'none', fontFamily: T.font, boxSizing: 'border-box',
  transition: 'border-color 0.15s, box-shadow 0.15s',
}
function Input({ value, onChange, placeholder, type = 'text', ...props }) {
  const [focus, setFocus] = useState(false)
  return (
    <input
      type={type} value={value} onChange={onChange} placeholder={placeholder}
      style={{ ...inputSx, ...(focus ? { borderColor: T.teal500, boxShadow: `0 0 0 3px ${T.teal100}`, background: T.white } : {}) }}
      onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
      {...props}
    />
  )
}
function Select({ value, onChange, children }) {
  const [focus, setFocus] = useState(false)
  return (
    <select
      value={value} onChange={onChange}
      style={{ ...inputSx, cursor: 'pointer', ...(focus ? { borderColor: T.teal500, boxShadow: `0 0 0 3px ${T.teal100}`, background: T.white } : {}) }}
      onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
    >{children}</select>
  )
}
function Textarea({ value, onChange, placeholder, rows = 3 }) {
  const [focus, setFocus] = useState(false)
  return (
    <textarea
      value={value} onChange={onChange} placeholder={placeholder} rows={rows}
      style={{ ...inputSx, resize: 'vertical', ...(focus ? { borderColor: T.teal500, boxShadow: `0 0 0 3px ${T.teal100}`, background: T.white } : {}) }}
      onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
    />
  )
}

// ─── Quick action button ───────────────────────────────────────────────────────
function QuickAction({ icon, label, color, onClick }) {
  const [hover, setHover] = useState(false)
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
        gap: 6, padding: '12px 8px', borderRadius: 10,
        background: hover ? color + '18' : T.gray50,
        border: `1.5px solid ${hover ? color : T.gray200}`,
        cursor: 'pointer', transition: 'all 0.15s', fontFamily: T.font,
      }}
    >
      <span style={{ fontSize: 20 }}>{icon}</span>
      <span style={{ fontSize: 11, fontWeight: 600, color: hover ? color : T.gray600, textAlign: 'center', lineHeight: 1.3 }}>{label}</span>
    </button>
  )
}

// ─── Resolves a photo_url to a displayable src ────────────────────────────────
// Backward-compatible: existing base64 data URIs are used as-is; storage paths
// get a 1-hour signed URL generated on mount.
function PhotoImg({ photo_url, alt, style, ...rest }) {
  const [src, setSrc] = useState(null)
  useEffect(() => {
    if (!photo_url) return
    if (photo_url.startsWith('data:')) { setSrc(photo_url); return }
    supabase.storage.from('patient-photos').createSignedUrl(photo_url, 3600)
      .then(({ data }) => { if (data?.signedUrl) setSrc(data.signedUrl) })
  }, [photo_url])
  return <img src={src || ''} alt={alt} style={style} {...rest} />
}

// ═══════════════════════════════════════════════════════════════════════════════
// SKIN PHOTO TRACKER TAB
// ═══════════════════════════════════════════════════════════════════════════════
function PhotosTab({ patientId, patientName }) {
  const { userProfile, can, isAdmin } = useAuth()
  const today = new Date().toISOString().split('T')[0]
  const [photos, setPhotos]           = useState([])
  const [loading, setLoading]         = useState(true)
  const [showUpload, setShowUpload]   = useState(false)
  const [lightboxIdx, setLightboxIdx] = useState(null)
  const [compareMode, setCompareMode] = useState(false)
  const [compareSet, setCompareSet]   = useState([])
  const [uploading, setUploading]     = useState(false)
  const [uploadErr, setUploadErr]     = useState('')
  const [preview, setPreview]         = useState(null)
  const [file, setFile]               = useState(null)
  const [bodyArea, setBodyArea]       = useState('')
  const [visitDate, setVisitDate]     = useState(today)
  const [photoNote, setPhotoNote]     = useState('')
  const fileRef = useRef(null)

  useEffect(() => {
    setLoading(true)
    supabase.from('patient_photos')
      .select('*')
      .eq('patient_id', patientId)
      .order('visit_date', { ascending: false })
      .then(({ data }) => { setPhotos(data || []); setLoading(false) })
  }, [patientId])

  function handleFileChange(e) {
    const f = e.target.files[0]
    if (!f) return
    const ALLOWED = ['image/jpeg', 'image/png', 'image/webp']
    if (!ALLOWED.includes(f.type)) { alert('Only JPG, PNG or WebP images are allowed'); return }
    if (f.size > 5 * 1024 * 1024) { alert('Photo must be under 5 MB'); return }
    setFile(f)
    setPreview(URL.createObjectURL(f))
  }

  async function handleUpload() {
    if (!file)      { setUploadErr('Please select a photo'); return }
    if (!bodyArea)  { setUploadErr('Please select a body area'); return }
    setUploading(true); setUploadErr('')
    const ext = file.type === 'image/jpeg' ? 'jpg' : file.type === 'image/png' ? 'png' : 'webp'
    const path = `${patientId}/${Date.now()}.${ext}`
    const { error: storageErr } = await supabase.storage.from('patient-photos').upload(path, file)
    if (storageErr) { setUploadErr(storageErr.message); setUploading(false); return }
    const { data, error } = await supabase.from('patient_photos').insert({
      patient_id: patientId, photo_url: path, clinic_id: userProfile?.clinic_id,
      body_area: bodyArea, visit_date: visitDate, notes: photoNote,
    }).select().single()
    setUploading(false)
    if (error) { setUploadErr(friendlyError(error)); return }
    setPhotos(prev => [data, ...prev])
    setShowUpload(false)
    setPreview(null); setFile(null); setBodyArea(''); setVisitDate(today); setPhotoNote('')
  }

  function toggleCompare(photo) {
    setCompareSet(prev => {
      if (prev.find(p => p.id === photo.id)) return prev.filter(p => p.id !== photo.id)
      if (prev.length >= 2) return [prev[1], photo]
      return [...prev, photo]
    })
  }

  async function handleDelete(photoId) {
    const { error } = await supabase.from('patient_photos').delete().eq('id', photoId)
    if (error) { alert(friendlyError(error)); return }
    setPhotos(prev => prev.filter(p => p.id !== photoId))
    setLightboxIdx(null)
  }

  async function downloadPhoto(photo) {
    let href = photo.photo_url
    if (!href.startsWith('data:')) {
      const { data } = await supabase.storage.from('patient-photos').createSignedUrl(photo.photo_url, 60)
      if (data?.signedUrl) href = data.signedUrl
    }
    const a = document.createElement('a')
    a.href = href
    a.download = `${patientName}-${photo.body_area}-${photo.visit_date}.jpg`
    a.click()
  }

  // Group by visit_date
  const grouped = photos.reduce((acc, p) => {
    if (!acc[p.visit_date]) acc[p.visit_date] = []
    acc[p.visit_date].push(p)
    return acc
  }, {})
  const sortedDates = Object.keys(grouped).sort((a, b) => b.localeCompare(a))
  const flat = sortedDates.flatMap(d => grouped[d])

  const inp = {
    width: '100%', padding: '7px 9px', border: `1.5px solid ${T.gray200}`,
    borderRadius: 7, fontSize: 12.5, outline: 'none', background: T.white,
    fontFamily: T.font, boxSizing: 'border-box', color: T.gray900,
  }

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 12, color: T.gray400, fontWeight: 500 }}>
          {photos.length} photo{photos.length !== 1 ? 's' : ''}
        </span>
        <div style={{ display: 'flex', gap: 6 }}>
          {photos.length >= 2 && (
            <button onClick={() => { setCompareMode(m => !m); setCompareSet([]) }} style={{
              padding: '5px 10px', borderRadius: 7,
              border: `1.5px solid ${compareMode ? T.teal500 : T.gray200}`,
              background: compareMode ? T.teal50 : T.white,
              color: compareMode ? T.teal700 : T.gray500,
              fontSize: 12, cursor: 'pointer', fontWeight: 600, fontFamily: T.font,
            }}>⚖️ Compare</button>
          )}
          {can('photos.upload') && <button onClick={() => setShowUpload(m => !m)} style={{
            padding: '5px 12px', borderRadius: 7, border: 'none',
            background: showUpload ? T.gray200 : T.teal600,
            color: showUpload ? T.gray700 : T.white,
            fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: T.font,
          }}>{showUpload ? 'Cancel' : '+ Add Photos'}</button>}
        </div>
      </div>

      {/* Compare hint */}
      {compareMode && (
        <div style={{
          background: T.teal50, border: `1px solid ${T.teal100}`, borderRadius: 8,
          padding: '8px 12px', fontSize: 12, color: T.teal700, marginBottom: 10,
        }}>
          {compareSet.length === 0 && '👆 Tap 2 photos to compare'}
          {compareSet.length === 1 && '👆 Select one more photo'}
          {compareSet.length === 2 && '✅ Comparing below — tap photos to change'}
        </div>
      )}

      {/* Before/After comparison view */}
      {compareMode && compareSet.length === 2 && (
        <div style={{
          background: T.gray900, borderRadius: 10, padding: 10, marginBottom: 14,
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {compareSet.map((p, i) => (
              <div key={p.id}>
                <div style={{
                  fontSize: 10, fontWeight: 700, color: i === 0 ? T.teal200 : '#f9a8d4',
                  textAlign: 'center', marginBottom: 5, letterSpacing: '.5px',
                }}>
                  {i === 0 ? 'BEFORE' : 'AFTER'}
                </div>
                <PhotoImg photo_url={p.photo_url} alt={p.body_area} style={{
                  width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 7,
                }} />
                <div style={{ fontSize: 10, color: T.gray400, textAlign: 'center', marginTop: 4 }}>
                  {fmtDate(p.visit_date)} · {p.body_area}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload form */}
      {showUpload && (
        <div style={{
          background: T.gray50, border: `1.5px solid ${T.gray200}`, borderRadius: 10,
          padding: 14, marginBottom: 14,
        }}>
          {uploadErr && (
            <div style={{
              background: T.red50, border: '1px solid #fecdd3', borderRadius: 7,
              padding: '7px 10px', color: T.red600, fontSize: 12, marginBottom: 10,
            }}>{uploadErr}</div>
          )}

          {/* Drop zone */}
          <div onClick={() => fileRef.current?.click()} style={{
            border: `2px dashed ${preview ? T.teal400 : T.gray300}`,
            borderRadius: 8, padding: 12, textAlign: 'center', cursor: 'pointer',
            background: T.white, marginBottom: 10, minHeight: 80,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {preview ? (
              <img src={preview} alt="preview" style={{
                maxWidth: '100%', maxHeight: 130, borderRadius: 6, objectFit: 'contain',
              }} />
            ) : (
              <div>
                <div style={{ fontSize: 28, marginBottom: 4 }}>📷</div>
                <div style={{ fontSize: 12, color: T.gray500, fontWeight: 600 }}>Click to select photo</div>
                <div style={{ fontSize: 11, color: T.gray400 }}>JPG, PNG or WebP</div>
              </div>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
            onChange={handleFileChange} style={{ display: 'none' }} />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: T.gray600, display: 'block', marginBottom: 3 }}>Body Area *</label>
              <select value={bodyArea} onChange={e => setBodyArea(e.target.value)} style={inp}>
                <option value="">Select…</option>
                {BODY_AREAS.map(a => <option key={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: T.gray600, display: 'block', marginBottom: 3 }}>Visit Date</label>
              <input type="date" value={visitDate} onChange={e => setVisitDate(e.target.value)} style={inp} />
            </div>
          </div>

          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: T.gray600, display: 'block', marginBottom: 3 }}>Doctor's Note</label>
            <textarea value={photoNote} onChange={e => setPhotoNote(e.target.value)}
              placeholder="e.g. Post-peel day 3, redness reducing…"
              rows={2} style={{ ...inp, resize: 'none' }} />
          </div>

          <button onClick={handleUpload} disabled={uploading} style={{
            width: '100%', padding: '9px', borderRadius: 7, border: 'none',
            background: uploading ? T.teal400 : T.teal700, color: T.white,
            fontSize: 13, fontWeight: 700, cursor: uploading ? 'not-allowed' : 'pointer',
            fontFamily: T.font,
          }}>{uploading ? 'Saving…' : '💾 Save Photo'}</button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '28px 0', color: T.gray400, fontSize: 13 }}>
          Loading photos…
        </div>
      )}

      {/* Empty state */}
      {!loading && photos.length === 0 && !showUpload && (
        <div style={{ textAlign: 'center', padding: '32px 0' }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>📷</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.gray600 }}>No photos yet</div>
          <div style={{ fontSize: 12, color: T.gray400, marginTop: 4 }}>
            Track skin progress with before/after photos
          </div>
        </div>
      )}

      {/* Gallery grouped by date */}
      {!loading && sortedDates.map(date => (
        <div key={date} style={{ marginBottom: 16 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: T.gray400, textTransform: 'uppercase',
            letterSpacing: '.5px', marginBottom: 7,
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <span style={{
              width: 6, height: 6, borderRadius: '50%',
              background: T.teal400, display: 'inline-block', flexShrink: 0,
            }} />
            {fmtDate(date)}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 5 }}>
            {grouped[date].map(photo => {
              const flatIdx = flat.indexOf(photo)
              const isSelected = compareSet.find(p => p.id === photo.id)
              return (
                <div key={photo.id}
                  onClick={() => compareMode ? toggleCompare(photo) : setLightboxIdx(flatIdx)}
                  style={{ position: 'relative', cursor: 'pointer', borderRadius: 8, overflow: 'hidden' }}>
                  <PhotoImg photo_url={photo.photo_url} alt={photo.body_area} style={{
                    width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block',
                    border: `2.5px solid ${isSelected ? T.teal500 : 'transparent'}`,
                    borderRadius: 8, transition: 'border-color .15s',
                  }} />
                  {/* Label overlay */}
                  <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    background: 'linear-gradient(transparent, rgba(0,0,0,0.68))',
                    padding: '10px 4px 4px', borderRadius: '0 0 6px 6px',
                  }}>
                    <div style={{ fontSize: 9.5, color: '#fff', fontWeight: 700, textAlign: 'center' }}>
                      {photo.body_area}
                    </div>
                  </div>
                  {/* Compare number badge */}
                  {isSelected && (
                    <div style={{
                      position: 'absolute', top: 4, right: 4,
                      background: T.teal600, borderRadius: '50%',
                      width: 18, height: 18, fontSize: 10, fontWeight: 800,
                      color: T.white, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {compareSet.findIndex(p => p.id === photo.id) + 1}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      ))}

      {/* Lightbox */}
      {lightboxIdx !== null && flat[lightboxIdx] && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.94)',
          zIndex: 1000, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', fontFamily: T.font,
        }} onClick={() => setLightboxIdx(null)}>

          {/* Header */}
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10,
            padding: '16px 20px',
            background: 'linear-gradient(rgba(0,0,0,0.7), transparent)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          }} onClick={e => e.stopPropagation()}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{patientName}</div>
              <div style={{ fontSize: 12, color: T.teal200, marginTop: 2 }}>
                {flat[lightboxIdx].body_area} · {fmtDate(flat[lightboxIdx].visit_date)}
              </div>
              <div style={{ fontSize: 12, color: T.gray400, marginTop: 1 }}>
                {lightboxIdx + 1} / {flat.length}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => downloadPhoto(flat[lightboxIdx])} style={{
                padding: '6px 14px', borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.25)',
                background: 'rgba(255,255,255,0.1)', color: '#fff',
                fontSize: 13, cursor: 'pointer', fontFamily: T.font,
              }}>⬇️ Download</button>
              {isAdmin && <button onClick={e => { e.stopPropagation(); handleDelete(flat[lightboxIdx].id) }} style={{
                padding: '6px 14px', borderRadius: 8,
                border: '1px solid rgba(220,38,38,0.4)',
                background: 'rgba(220,38,38,0.15)', color: '#fca5a5',
                fontSize: 13, cursor: 'pointer', fontFamily: T.font,
              }}>🗑</button>}
              <button onClick={() => setLightboxIdx(null)} style={{
                width: 34, height: 34, borderRadius: '50%', border: 'none',
                background: 'rgba(255,255,255,0.15)', color: '#fff',
                fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>✕</button>
            </div>
          </div>

          {/* Prev */}
          {lightboxIdx > 0 && (
            <button onClick={e => { e.stopPropagation(); setLightboxIdx(i => i - 1) }} style={{
              position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
              width: 46, height: 46, borderRadius: '50%', border: 'none',
              background: 'rgba(255,255,255,0.15)', color: '#fff',
              fontSize: 26, cursor: 'pointer', zIndex: 10,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>‹</button>
          )}

          {/* Image */}
          <PhotoImg photo_url={flat[lightboxIdx].photo_url} alt={flat[lightboxIdx].body_area}
            onClick={e => e.stopPropagation()}
            style={{
              maxWidth: 'min(88vw, 820px)', maxHeight: '76vh',
              objectFit: 'contain', borderRadius: 10,
              boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
            }} />

          {/* Next */}
          {lightboxIdx < flat.length - 1 && (
            <button onClick={e => { e.stopPropagation(); setLightboxIdx(i => i + 1) }} style={{
              position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
              width: 46, height: 46, borderRadius: '50%', border: 'none',
              background: 'rgba(255,255,255,0.15)', color: '#fff',
              fontSize: 26, cursor: 'pointer', zIndex: 10,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>›</button>
          )}

          {/* Note footer */}
          {flat[lightboxIdx].notes && (
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 10,
              padding: '24px 24px 20px',
              background: 'linear-gradient(transparent, rgba(0,0,0,0.75))',
              textAlign: 'center',
            }} onClick={e => e.stopPropagation()}>
              <div style={{ fontSize: 13, color: T.teal100, fontStyle: 'italic' }}>
                "{flat[lightboxIdx].notes}"
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// ADD / EDIT MODAL
// ═══════════════════════════════════════════════════════════════════════════════
function PatientModal({ patient, onClose, onSaved }) {
  const { userProfile } = useAuth()
  const [sharedPhone, setSharedPhone] = useState([])
  const blank = {
    full_name: '', age: '', gender: '', phone: '', email: '',
    address: '', skin_type: '', blood_group: '', date_of_birth: '', allergies: '',
  }
  const [form, setForm]     = useState(patient ? {
    full_name:     patient.full_name     || '',
    age:           patient.age           || '',
    gender:        patient.gender        || '',
    phone:         patient.phone         || '',
    email:         patient.email         || '',
    address:       patient.address       || '',
    skin_type:     patient.skin_type     || '',
    blood_group:   patient.blood_group   || '',
    date_of_birth: patient.date_of_birth || '',
    allergies:     patient.allergies     || '',
  } : blank)
  const [saving, setSaving] = useState(false)
  const [error, setError]   = useState('')

  function set(k, v) { setForm(f => ({ ...f, [k]: v })) }

  // Family members can share one phone number — show who else uses it, but allow saving
  useEffect(() => {
    const phone = (form.phone || '').trim()
    if (phone.replace(/\D/g, '').length < 10) { setSharedPhone([]); return }
    const t = setTimeout(async () => {
      let q = supabase.from('patients').select('id, full_name, age').eq('phone', phone).order('full_name')
      if (patient?.id) q = q.neq('id', patient.id)
      const { data } = await q
      setSharedPhone(data || [])
    }, 350)
    return () => clearTimeout(t)
  }, [form.phone, patient?.id])

  async function handleSave(e) {
    e && e.preventDefault && e.preventDefault()
    if (!(form.full_name || '').trim()) { setError('Full name is required.'); return }
    if (!(form.phone || '').trim())     { setError('Phone number is required.'); return }
    setError(''); setSaving(true)
    try {
      const payload = {
        full_name:     (form.full_name || '').trim(),
        age:           form.age ? parseInt(form.age) : null,
        gender:        form.gender || null,
        phone:         (form.phone || '').trim(),
        email:         (form.email || '').trim() || null,
        address:       (form.address || '').trim() || null,
        skin_type:     form.skin_type || null,
        blood_group:   form.blood_group || null,
        allergies:     (form.allergies || '').trim() || null,
        date_of_birth: form.date_of_birth || null,
        clinic_id:     userProfile?.clinic_id,
      }
      let result
      if (patient?.id) {
        const { data, error } = await supabase.from('patients').update(payload).eq('id', patient.id).select().single()
        if (error) throw error
        result = data
      } else {
        const { data, error } = await supabase.from('patients').insert(payload).select().single()
        if (error) throw error
        result = data
      }
      onSaved(result)
    } catch (e) {
      setError(friendlyError(e, 'Save failed.'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(8,40,40,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 200, padding: 20, fontFamily: T.font,
    }} onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{
        background: T.white, borderRadius: 16, width: '100%', maxWidth: 600,
        maxHeight: '92vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
        boxShadow: '0 24px 64px rgba(0,0,0,0.35)',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: `1px solid ${T.gray100}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: T.teal900, borderRadius: '16px 16px 0 0',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: T.white }}>
              {patient ? 'Edit Patient' : 'Add New Patient'}
            </h2>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: T.teal200 }}>
              {patient ? `Editing ${patient.full_name}` : 'Register a new patient'}
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8,
            color: T.white, width: 32, height: 32, cursor: 'pointer',
            fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>×</button>
        </div>

        {/* Body */}
        <form onSubmit={handleSave} style={{ overflowY: 'auto', padding: '24px' }}>
          {error && (
            <div style={{
              background: T.red50, border: '1px solid #fecdd3', borderRadius: 8,
              padding: '10px 14px', color: T.red600, fontSize: 13, marginBottom: 16,
            }}>⚠️ {error}</div>
          )}

          {/* Row: name */}
          <Field label="Full Name *">
            <Input value={form.full_name} onChange={e => set('full_name', e.target.value)} placeholder="e.g. Priya Sharma" />
          </Field>

          {/* Row: age + gender */}
          <div style={{ display: 'flex', gap: 12 }}>
            <Field label="Age" half>
              <Input type="number" value={form.age} onChange={e => set('age', e.target.value)} placeholder="e.g. 28" />
            </Field>
            <Field label="Gender" half>
              <Select value={form.gender} onChange={e => set('gender', e.target.value)}>
                <option value="">Select gender</option>
                {GENDERS.map(g => <option key={g} value={g}>{g}</option>)}
              </Select>
            </Field>
          </div>

          {/* Date of Birth */}
          <Field label="Date of Birth">
            <Input type="date" value={form.date_of_birth || ''} onChange={e => set('date_of_birth', e.target.value)} />
          </Field>

          {/* Row: phone + email */}
          <div style={{ display: 'flex', gap: 12 }}>
            <Field label="Phone *" half>
              <Input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="e.g. 9876543210" />
            </Field>
            <Field label="Email" half>
              <Input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@example.com" />
            </Field>
          </div>
          {sharedPhone.length > 0 && (
            <div style={{ background: T.blue50, border: '1px solid #bfdbfe', borderRadius: 8, padding: '9px 12px', color: '#1d4ed8', fontSize: 12.5, marginTop: -6, marginBottom: 14 }}>
              ℹ️ {sharedPhone.length === 1 ? '1 patient already uses' : `${sharedPhone.length} patients already use`} this number: {sharedPhone.map(p => `${p.full_name}${p.age != null ? ` (${p.age})` : ''}`).join(', ')}. You can still save — family members may share a phone.
            </div>
          )}

          {/* Row: skin type + blood group */}
          <div style={{ display: 'flex', gap: 12 }}>
            <Field label="Skin Type" half>
              <Select value={form.skin_type} onChange={e => set('skin_type', e.target.value)}>
                <option value="">Select skin type</option>
                {SKIN_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
              </Select>
            </Field>
            <Field label="Blood Group" half>
              <Select value={form.blood_group} onChange={e => set('blood_group', e.target.value)}>
                <option value="">Select blood group</option>
                {BLOOD_GROUPS.map(b => <option key={b} value={b}>{b}</option>)}
              </Select>
            </Field>
          </div>

          {/* Address */}
          <Field label="Address">
            <Textarea value={form.address} onChange={e => set('address', e.target.value)} placeholder="Full address" rows={2} />
          </Field>

          {/* Allergies */}
          <Field label="Allergies">
            <Input value={form.allergies || ''} onChange={e => set('allergies', e.target.value)} placeholder="e.g. Penicillin, sulfa drugs…" />
          </Field>
        </form>

        {/* Footer */}
        <div style={{
          padding: '16px 24px', borderTop: `1px solid ${T.gray100}`,
          display: 'flex', justifyContent: 'flex-end', gap: 10,
        }}>
          <button onClick={onClose} style={{
            padding: '9px 20px', borderRadius: 8, border: `1.5px solid ${T.gray200}`,
            background: T.white, color: T.gray600, fontSize: 13, fontWeight: 600,
            cursor: 'pointer', fontFamily: T.font,
          }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{
            padding: '9px 24px', borderRadius: 8, border: 'none',
            background: saving ? T.teal400 : T.teal600, color: T.white,
            fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer',
            fontFamily: T.font, boxShadow: '0 2px 8px rgba(20,128,128,0.3)',
          }}>
            {saving ? 'Saving…' : patient ? 'Save Changes' : 'Add Patient'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// RX MODAL (patient-context — same as Appointments RxModal, no appt needed)
// ═══════════════════════════════════════════════════════════════════════════════
function PatientRxModal({ patient, onClose, onSaved, prefill = {} }) {
  const { userProfile, clinic, doctors } = useAuth()
  const [chosenDoctor, setChosenDoctor] = useState(prefill.doctorName || '')
  const prescriber = resolvePrescriber(userProfile, doctors, chosenDoctor)
  const patName  = patient?.full_name || 'Patient'
  const patPhone = (patient?.phone || '').replace(/\D/g, '')
  const today    = new Date()

  const [complaint, setComplaint] = useState(prefill.complaint || '')
  const [findings,  setFindings]  = useState(prefill.findings  || '')
  const [diagnosis, setDiagnosis] = useState(prefill.diagnosis || '')
  const [medicines, setMedicines] = useState(prefill.medicines?.length ? prefill.medicines : [{ name:'', form:'', dose:'', dur:'', instr:'' }])
  const [advice,    setAdvice]    = useState(prefill.advice    || [])
  const [followUp,  setFollowUp]  = useState(prefill.followUp  || '')
  const [rxNotes,   setRxNotes]   = useState(prefill.rxNotes   || '')
  const [saving,    setSaving]    = useState(false)
  const [savedToast,setSavedToast]= useState('')

  function addMed()        { setMedicines(m => [...m, { name:'', form:'', dose:'', dur:'', instr:'' }]) }
  function setMed(i, k, v) { setMedicines(m => m.map((r,j) => j===i ? { ...r, [k]: v } : r)) }
  function removeMed(i)    { setMedicines(m => m.filter((_,j) => j!==i)) }
  function toggleAdv(a)    { setAdvice(p => p.includes(a) ? p.filter(x=>x!==a) : [...p, a]) }

  function buildRxText() {
    return [
      complaint && `Chief Complaint: ${complaint}`,
      findings  && `Findings: ${findings}`,
      diagnosis && `Diagnosis: ${diagnosis}`,
      medicines.filter(m=>m.name).length && 'Rx:\n' + medicines.filter(m=>m.name).map(m=>`  • ${m.name}${m.form?' ('+m.form+')':''} — ${m.dose}${m.dur?' × '+m.dur:''}${m.instr?' ['+m.instr+']':''}`).join('\n'),
      advice.length && `Advice: ${advice.join(' · ')}`,
      followUp  && `Follow-up: ${followUp}`,
      rxNotes   && `Notes: ${rxNotes}`,
    ].filter(Boolean).join('\n\n')
  }

  async function saveRx() {
    const rxText = buildRxText()
    if (!rxText.trim()) { alert('Please fill in at least one field.'); return }
    if (!prescriber) { alert('Please choose the prescribing doctor.'); return }
    setSaving(true)
    try {
      // 1. Save formatted text to visit_notes (for the History timeline)
      const { data: noteRow, error: noteErr } = await supabase.from('visit_notes').insert({
        patient_id: patient.id,
        note_type:  'Prescription',
        note_text:  rxText,
        created_by: prescriber.full_name,
        clinic_id:  userProfile?.clinic_id,
      }).select('*').single()
      if (noteErr) throw noteErr

      // 2. Also save structured data to prescriptions table so "Open in Rx Pad"
      //    can re-populate all fields correctly next time.
      //    Use the same column names as Prescriptions.js for full compatibility.
      const { error: rxErr } = await supabase.from('prescriptions').insert({
        patient_id:           patient.id,
        patient_name:         patient.full_name || '',
        doctor_name:          prescriber.full_name,
        clinic_id:            userProfile?.clinic_id,
        chief_complaint:      complaint || null,
        examination_findings: findings  || null,
        diagnosis:            diagnosis || null,
        medicines:            medicines.filter(m => m.name.trim()).map(m => ({
          name:         m.name,
          formulation:  m.form,
          dosage:       m.dose,
          duration:     m.dur,
          instructions: m.instr,
        })),
        advice:               advice,
        follow_up:            followUp  || null,
        additional_notes:     rxNotes   || null,
      })
      if (rxErr) throw rxErr

      onSaved && onSaved(noteRow)
      const msg = prefill.editingDate ? 'New prescription saved. Previous visit unchanged.' : 'Saved to Visit History'
      setSavedToast(msg)
      setTimeout(() => setSavedToast(''), 2500)
    } catch(e) { alert(friendlyError(e, 'Save failed')) }
    setSaving(false)
  }

  function rxOutput() {
    return {
      clinic, doctor: prescriber,
      patient: { name: patName, age: patient?.age, gender: patient?.gender },
      date: today, complaint, findings, diagnosis, followUp, notes: rxNotes, advice,
      medicines: medicines.map(m => ({ name: m.name, formulation: m.form, dosage: m.dose, duration: m.dur, instructions: m.instr })),
    }
  }

  function sendWhatsApp() {
    if (!patPhone) { alert('No phone number on file.'); return }
    if (!prescriber) { alert('Please choose the prescribing doctor.'); return }
    window.open(`https://wa.me/91${patPhone}?text=${encodeURIComponent(buildRxWhatsApp(rxOutput()))}`, '_blank')
  }

  function printRx() {
    if (!prescriber) { alert('Please choose the prescribing doctor.'); return }
    printPrescription(rxOutput())
  }

  const tdInp = { ...mSx, fontSize: 12, padding: '5px 7px' }
  const thSx2 = { fontSize: 10, fontWeight: 700, color: T.gray500, textTransform: 'uppercase' }

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose() }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(8,40,40,0.65)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: T.font }}>
      <div style={{ background: T.white, borderRadius: 16, width: '100%', maxWidth: 700, maxHeight: '94vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,.35)', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ padding: '14px 20px', background: T.teal900, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.white }}>
              {prefill.editingDate ? `📋 Rx Pad — Editing visit from ${prefill.editingDate}` : '📋 Prescription Pad'}
            </div>
            <div style={{ fontSize: 11.5, color: T.teal200, marginTop: 2 }}>
              {patName} · {prescriber?.full_name || 'Doctor not selected'} · {today.toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}
            </div>
          </div>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 7, background: 'rgba(255,255,255,.14)', border: 'none', color: T.white, fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {/* Patient strip */}
          <div style={{ display: 'flex', gap: 16, padding: '10px 14px', background: T.teal50, borderRadius: 8, border: `1px solid ${T.teal100}`, marginBottom: 16 }}>
            {[['Patient', patName],['Age', patient?.age ? patient.age+' yrs' : '—'],['Gender', patient?.gender||'—']].map(([l,v]) => (
              <div key={l}><label style={{ fontSize: 9.5, fontWeight: 700, color: T.teal600, textTransform: 'uppercase', letterSpacing: '.5px', display: 'block' }}>{l}</label><div style={{ fontSize: 13, fontWeight: 600, color: T.gray900 }}>{v}</div></div>
            ))}
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 9.5, fontWeight: 700, color: T.teal600, textTransform: 'uppercase', letterSpacing: '.5px', display: 'block' }}>Prescribing Doctor</label>
              {userProfile?.registration_no ? (
                <div style={{ fontSize: 13, fontWeight: 600, color: T.gray900 }}>{userProfile.full_name}</div>
              ) : (
                <select value={prescriber?.full_name || ''} onChange={e => setChosenDoctor(e.target.value)}
                  style={{ ...mSx, padding: '4px 8px', fontSize: 12.5, background: T.white }}>
                  <option value="">{doctors.length ? 'Select doctor…' : 'No registered doctors'}</option>
                  {doctors.map(d => <option key={d.id} value={d.full_name}>{d.full_name}</option>)}
                </select>
              )}
              {prescriber && <div style={{ fontSize: 10.5, color: T.gray500, marginTop: 2 }}>{[prescriber.qualification, regLine(prescriber)].filter(Boolean).join(' · ')}</div>}
            </div>
          </div>

          {/* Clinical fields */}
          <div style={{ display: 'grid', gap: 12, marginBottom: 16 }}>
            {[['Chief Complaint', complaint, setComplaint, "Patient's main complaint…"],['Examination Findings', findings, setFindings, 'Examination findings…']].map(([label, val, setter, ph]) => (
              <div key={label}><MLabel>{label}</MLabel>
                <textarea value={val} onChange={e => setter(e.target.value)} rows={2} placeholder={ph} style={{ ...mSx, resize: 'vertical' }}
                  onFocus={e => { e.target.style.borderColor=T.teal500; e.target.style.boxShadow=`0 0 0 3px ${T.teal100}` }}
                  onBlur={e =>  { e.target.style.borderColor=T.gray200;  e.target.style.boxShadow='none' }} />
              </div>
            ))}
            <div><MLabel>Diagnosis</MLabel><MInput value={diagnosis} onChange={e => setDiagnosis(e.target.value)} placeholder="Primary diagnosis…" /></div>
          </div>

          {/* Medicines */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontSize: 24, fontWeight: 800, color: T.teal800, fontFamily: 'serif', lineHeight: 1 }}>℞</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: T.gray500, textTransform: 'uppercase', letterSpacing: '.5px' }}>Medicines</span>
              </div>
              <button onClick={addMed} style={{ fontSize: 11.5, color: T.teal600, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, fontFamily: T.font }}>+ Add Medicine</button>
            </div>
            <div style={{ border: `1px solid ${T.gray200}`, borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1.5fr 28px', background: T.gray50, padding: '6px 10px', gap: 6 }}>
                {['Medicine','Form','Dosage','Duration','Instructions',''].map(h => <div key={h} style={thSx2}>{h}</div>)}
              </div>
              {medicines.map((m, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1.5fr 28px', padding: '5px 10px', gap: 6, borderTop: `1px solid ${T.gray100}`, alignItems: 'center' }}>
                  <input value={m.name}  onChange={e => setMed(i,'name', e.target.value)} placeholder="e.g. Clindamycin" style={tdInp} />
                  <input value={m.form}  onChange={e => setMed(i,'form', e.target.value)} placeholder="Gel/Tab"          style={tdInp} />
                  <input value={m.dose}  onChange={e => setMed(i,'dose', e.target.value)} placeholder="OD / BD"          style={tdInp} />
                  <input value={m.dur}   onChange={e => setMed(i,'dur',  e.target.value)} placeholder="7 days"           style={tdInp} />
                  <input value={m.instr} onChange={e => setMed(i,'instr',e.target.value)} placeholder="After meals"      style={tdInp} />
                  <button onClick={() => removeMed(i)} disabled={medicines.length===1} style={{ background:'none', border:'none', color: medicines.length===1?T.gray200:'#dc2626', cursor: medicines.length===1?'default':'pointer', fontSize:16, fontFamily:T.font }}>×</button>
                </div>
              ))}
            </div>
          </div>

          {/* Advice */}
          <div style={{ marginBottom: 14 }}>
            <MLabel>Advice &amp; Lifestyle</MLabel>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {ADVICE_OPTIONS.map(a => {
                const on = advice.includes(a)
                return (
                  <button key={a} onClick={() => toggleAdv(a)} style={{ padding: '5px 11px', borderRadius: 20, fontSize: 11.5, fontWeight: on?700:500, border: `1.5px solid ${on?T.teal500:T.gray300}`, background: on?T.teal50:T.white, color: on?T.teal700:T.gray500, cursor: 'pointer', fontFamily: T.font, transition: 'all .12s' }}>
                    {on ? '✓ ' : ''}{a}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Follow-up + notes */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
            <div><MLabel>Follow-up</MLabel>
              <MSelect value={followUp} onChange={e => setFollowUp(e.target.value)}>
                <option value="">Select…</option>
                {RX_FOLLOW_UP.map(f => <option key={f} value={f}>{f}</option>)}
              </MSelect>
            </div>
            <div><MLabel>Additional Notes</MLabel><MInput value={rxNotes} onChange={e => setRxNotes(e.target.value)} placeholder="Any additional notes…" /></div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${T.gray100}`, display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0, position: 'relative' }}>
          {savedToast && (
            <div style={{ position: 'absolute', top: -46, left: '50%', transform: 'translateX(-50%)', background: '#16a34a', color: '#fff', fontSize: 13, fontWeight: 700, padding: '8px 20px', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,.18)', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
              ✓ {savedToast}
            </div>
          )}
          <button onClick={sendWhatsApp} style={{ padding: '7px 13px', borderRadius: 8, border: '1.5px solid #22c55e', background: '#dcfce7', color: '#166534', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: T.font }}>💬 WhatsApp Rx</button>
          <button onClick={printRx}      style={{ padding: '7px 13px', borderRadius: 8, border: `1.5px solid ${T.gray200}`, background: T.white, color: T.gray600, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: T.font }}>🖨️ Print Rx</button>
          <button onClick={onClose}      style={{ marginLeft: 'auto', padding: '8px 16px', borderRadius: 8, border: `1.5px solid ${T.gray200}`, background: T.white, color: T.gray600, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: T.font }}>Cancel</button>
          <button onClick={saveRx} disabled={saving}
            style={{ padding: '8px 22px', borderRadius: 8, border: 'none', background: saving ? T.teal200 : savedToast ? '#16a34a' : T.teal600, color: T.white, fontSize: 13, fontWeight: 700, cursor: saving ? 'default' : 'pointer', fontFamily: T.font, transition: 'background .25s' }}>
            {saving ? 'Saving…' : savedToast ? '✓ Saved' : 'Save to Visit History'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// PATIENT PROFILE PANEL
// ═══════════════════════════════════════════════════════════════════════════════
function ProfilePanel({ patient, onClose, onEdit, onDeleted, onPatientUpdated }) {
  const { userProfile, can, isAdmin, role } = useAuth()
  const [visits, setVisits]               = useState([])
  const [visitNotes, setVisitNotes]       = useState([])
  const [prescriptions, setPrescriptions] = useState([])
  const [invoices, setInvoices]           = useState([])
  const [visitsLoading, setVLoading]      = useState(true)
  const [activeTab, setActiveTab]         = useState('visits')
  const [confirmDelete, setConfirmDel]    = useState(false)
  const [deleting, setDeleting]           = useState(false)
  const [expandedItem, setExpanded]       = useState(null)

  // FIX 3: action modal states
  const [addNoteOpen, setAddNoteOpen]     = useState(false)
  const [newNote, setNewNote]             = useState('')
  const [savingNote, setSavingNote]       = useState(false)
  const [rxModal, setRxModal]             = useState(null)   // null=closed, {}=fresh, {…}=prefilled
  const [bookNextOpen, setBookNextOpen]   = useState(false)
  const [bookForm, setBookForm]           = useState({ date: '', time: '', service: '' })
  const [bookingSaving, setBookingSaving] = useState(false)
  const [invModalOpen, setInvModalOpen]   = useState(false)

  // FIX 4: editable details states
  const [editingDetails, setEditingDetails] = useState(false)
  const [detailEdits, setDetailEdits]       = useState({})
  const [savingDetails, setSavingDetails]   = useState(false)

  const [bg, fg] = avatarColor(patient.full_name)

  // inline style for small inputs in modals/details
  const inp2 = {
    padding: '7px 10px', border: `1.5px solid ${T.gray200}`, borderRadius: 7,
    fontSize: 13, color: T.gray900, background: T.white, outline: 'none',
    fontFamily: T.font, boxSizing: 'border-box', width: '100%',
  }

  // Visit notes & prescriptions are clinical records (admin/doctor only)
  const clinical = can('clinical.view')
  const loadData = useCallback(() => {
    setVLoading(true)
    const none = Promise.resolve({ data: [] })
    Promise.all([
      supabase.from('appointments').select('*').eq('patient_id', patient.id).order('date', { ascending: false }),
      clinical ? supabase.from('visit_notes').select('*').eq('patient_id', patient.id).order('created_at', { ascending: false }).limit(30) : none,
      supabase.from('invoices').select('*').eq('patient_id', patient.id).order('created_at', { ascending: false }).limit(20),
      clinical ? supabase.from('prescriptions').select('*').eq('patient_id', patient.id).order('created_at', { ascending: false }) : none,
    ]).then(([apptRes, notesRes, invRes, rxRes]) => {
      setVisits(apptRes.data || [])
      setVisitNotes(notesRes.data || [])
      setInvoices(invRes.data || [])
      setPrescriptions(rxRes.data || [])
      setVLoading(false)
    })
  }, [patient.id, clinical])

  useEffect(() => { loadData() }, [loadData])

  async function handleDelete() {
    if (!isAdmin) { alert("You don't have access to this."); return }
    setDeleting(true)
    const { error } = await supabase.from('patients').delete().eq('id', patient.id)
    if (!error) onDeleted(patient.id)
    else { setDeleting(false); alert(friendlyError(error, 'Delete failed')) }
  }

  // FIX 3: save visit note
  async function saveNote() {
    if (!newNote.trim()) return
    setSavingNote(true)
    const { error } = await supabase.from('visit_notes').insert({
      patient_id: patient.id,
      note_text:  newNote.trim(),
      note_type:  'Note',
      created_by: userProfile?.full_name || null,
      clinic_id:  userProfile?.clinic_id,
    })
    if (error) { alert(friendlyError(error)); setSavingNote(false); return }
    const { data } = await supabase.from('visit_notes').select('*').eq('patient_id', patient.id).order('created_at', { ascending: false }).limit(30)
    setVisitNotes(data || [])
    setSavingNote(false)
    setAddNoteOpen(false)
    setNewNote('')
  }

  // FIX 3: book next appointment
  async function saveBookNext() {
    if (!bookForm.date) return
    setBookingSaving(true)
    const { error } = await supabase.from('appointments').insert({
      patient_id:       patient.id,
      clinic_id:        userProfile?.clinic_id,
      date:             bookForm.date,
      time:             bookForm.time || null,
      appointment_type: 'Follow-up',
      service_type:     bookForm.service || 'Consultation',
      status:           'Scheduled',
    })
    if (error) { alert(friendlyError(error)); setBookingSaving(false); return }
    const { data } = await supabase.from('appointments').select('*').eq('patient_id', patient.id).order('date', { ascending: false })
    setVisits(data || [])
    setBookingSaving(false)
    setBookNextOpen(false)
    setBookForm({ date: '', time: '', service: '' })
  }

  // FIX 3: open Rx Pad pre-filled from a prescriptions-table row or visit_note
  async function openRxPadWithData(item) {
    let source = item

    if (item._type === 'rx' && item.id) {
      // Direct fetch by ID — we already know the prescriptions row id
      const { data: rxRow } = await supabase
        .from('prescriptions')
        .select('*')
        .eq('id', item.id)
        .maybeSingle()
      if (rxRow) source = rxRow
    } else {
      // visit_note with note_type=Prescription: try to find same-day prescriptions row
      const dateStr = (item.created_at || '').split('T')[0] || isoToday()
      const { data: rxRow } = await supabase
        .from('prescriptions')
        .select('*')
        .eq('patient_id', patient.id)
        .gte('created_at', dateStr + 'T00:00:00+05:30')
        .lte('created_at', dateStr + 'T23:59:59+05:30')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      if (rxRow) source = rxRow
    }

    const dateStr = (source.created_at || item.created_at || '').split('T')[0] || isoToday()

    // Parse medicines — normalise field names to what PatientRxModal expects
    let meds = []
    try {
      meds = Array.isArray(source.medicines)
        ? source.medicines
        : JSON.parse(source.medicines || '[]')
    } catch { meds = [] }
    meds = meds.map(m => ({
      name:  m.name  || m.medicine_name || '',
      form:  m.form  || m.formulation   || '',
      dose:  m.dose  || m.dosage        || '',
      dur:   m.dur   || m.duration      || '',
      instr: m.instr || m.instructions  || '',
    }))
    if (!meds.length) meds = [{ name: '', form: '', dose: '', dur: '', instr: '' }]

    // Parse advice
    let adv = []
    try {
      adv = Array.isArray(source.advice)
        ? source.advice
        : JSON.parse(source.advice || '[]')
    } catch { adv = [] }

    // If this is a legacy visit_note with no prescriptions counterpart,
    // parse the plain-text note_text into individual fields instead of
    // dumping the whole string into rxNotes.
    let legacyNoteText = ''
    if (!source.chief_complaint && !source.diagnosis && source.note_text) {
      legacyNoteText = source.note_text
    }

    function extractSection(text, ...labels) {
      for (const label of labels) {
        const re = new RegExp(label + ':?\\s*([^\\n]+(?:\\n(?![A-Z][^:]+:)[^\\n]*)*)', 'i')
        const m = text.match(re)
        if (m) return m[1].trim()
      }
      return ''
    }

    // *** Use the exact key names PatientRxModal reads from `prefill` ***
    setRxModal({
      editingDate: dateStr,
      doctorName:  source.doctor_name || '',
      complaint:   source.chief_complaint || extractSection(legacyNoteText, 'Chief Complaint', 'Complaint'),
      findings:    source.examination_findings || source.examination || extractSection(legacyNoteText, 'Examination Findings', 'Findings'),
      diagnosis:   source.diagnosis            || extractSection(legacyNoteText, 'Diagnosis'),
      medicines:   meds,
      advice:      adv,
      followUp:    source.follow_up            || extractSection(legacyNoteText, 'Follow-up', 'Followup'),
      // Only put additional_notes in rxNotes — NOT the whole note_text
      rxNotes:     source.additional_notes     || extractSection(legacyNoteText, 'Notes'),
    })
  }

  // FIX 4: save edited details to Supabase
  async function saveDetails() {
    setSavingDetails(true)
    const payload = {}
    if (detailEdits.phone !== undefined)         payload.phone         = detailEdits.phone
    if (detailEdits.age !== undefined)           payload.age           = detailEdits.age ? parseInt(detailEdits.age) : null
    if (detailEdits.gender !== undefined)        payload.gender        = detailEdits.gender || null
    if (detailEdits.blood_group !== undefined)   payload.blood_group   = detailEdits.blood_group || null
    if (detailEdits.skin_type !== undefined)     payload.skin_type     = detailEdits.skin_type || null
    if (detailEdits.email !== undefined)         payload.email         = detailEdits.email || null
    if (detailEdits.allergies !== undefined)     payload.allergies     = detailEdits.allergies || null
    if (detailEdits.date_of_birth !== undefined) payload.date_of_birth = detailEdits.date_of_birth || null
    const { data, error } = await supabase.from('patients').update(payload).eq('id', patient.id).select().single()
    setSavingDetails(false)
    if (error) { alert(friendlyError(error)); return }
    if (data) {
      setEditingDetails(false)
      if (onPatientUpdated) onPatientUpdated(data)
    }
  }

  function startEditDetails() {
    setDetailEdits({
      phone:         patient.phone         || '',
      age:           patient.age           ?? '',
      gender:        patient.gender        || '',
      blood_group:   patient.blood_group   || '',
      skin_type:     patient.skin_type     || '',
      email:         patient.email         || '',
      allergies:     patient.allergies     || '',
      date_of_birth: patient.date_of_birth || '',
    })
    setEditingDetails(true)
  }

  const totalHistoryCount = visits.length + visitNotes.length + prescriptions.length

  return (
    <div style={{
      width: 380, flexShrink: 0, background: T.white,
      borderLeft: `1px solid ${T.gray200}`,
      display: 'flex', flexDirection: 'column', height: '100%',
      fontFamily: T.font, overflowY: 'auto',
      animation: 'slideIn 0.22s ease',
    }}>
      <style>{`@keyframes slideIn { from { transform: translateX(40px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>

      {/* Close bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 18px', borderBottom: `1px solid ${T.gray100}`,
        position: 'sticky', top: 0, background: T.white, zIndex: 10,
      }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: T.gray700 }}>Patient Profile</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => onEdit(patient)} style={{
            padding: '5px 12px', borderRadius: 7, border: `1.5px solid ${T.teal500}`,
            background: T.teal50, color: T.teal700, fontSize: 12, fontWeight: 600,
            cursor: 'pointer', fontFamily: T.font,
          }}>✏️ Edit</button>
          <button onClick={onClose} style={{
            background: T.gray100, border: 'none', borderRadius: 7,
            width: 30, height: 30, cursor: 'pointer', fontSize: 16,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>×</button>
        </div>
      </div>

      {/* Avatar + name */}
      <div style={{
        padding: '24px 20px 20px', background: `linear-gradient(135deg, ${bg}, ${bg}dd)`,
        display: 'flex', alignItems: 'center', gap: 16,
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%',
          background: `linear-gradient(135deg, ${fg}44, ${fg}88)`,
          border: `3px solid ${fg}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 22, fontWeight: 800, color: T.white, flexShrink: 0,
        }}>
          {initials(patient.full_name)}
        </div>
        <div>
          <div style={{ fontSize: 17, fontWeight: 800, color: T.white }}>{patient.full_name}</div>
          <div style={{ fontSize: 12, color: fg + 'cc', marginTop: 3 }}>
            {[patient.age && `${patient.age} yrs`, patient.gender].filter(Boolean).join(' · ')}
          </div>
          <div style={{ fontSize: 12, color: fg + 'bb', marginTop: 2 }}>{patient.phone}</div>
        </div>
      </div>

      {/* FIX 3: Action buttons */}
      <div style={{ padding: '14px 16px', borderBottom: `1px solid ${T.gray100}` }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <QuickAction icon="📅" label="Book Next" color={T.teal600}
            onClick={() => setBookNextOpen(true)} />
          {can('notes.write') && <QuickAction icon="📝" label="Add Note" color={T.blue600}
            onClick={() => { setAddNoteOpen(true); setActiveTab('visits') }} />}
          {can('prescriptions.write') && <QuickAction icon="💊" label="Rx Pad" color="#7c3aed"
            onClick={() => setRxModal({})} />}
          {can('invoices.create') && <QuickAction icon="🧾" label="Invoice" color="#8b5cf6"
            onClick={() => setInvModalOpen(true)} />}
          <QuickAction icon="💬" label="WhatsApp" color="#25d366"
            onClick={() => window.open(whatsappURL(patient.phone), '_blank')} />
        </div>
      </div>

      {/* Tabs + content */}
      <div style={{ padding: '0 16px 16px' }}>
        {/* Tab bar */}
        <div style={{ display: 'flex', borderBottom: `2px solid ${T.gray100}`, marginBottom: 14, marginTop: 4, overflowX: 'auto' }}>
          {['visits', 'invoices', 'details', 'photos'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{
              padding: '10px 8px', background: 'none', border: 'none',
              fontFamily: T.font, fontSize: 12, fontWeight: activeTab === tab ? 700 : 500,
              color: activeTab === tab ? T.teal600 : T.gray400,
              borderBottom: activeTab === tab ? `2px solid ${T.teal500}` : '2px solid transparent',
              marginBottom: -2, cursor: 'pointer', whiteSpace: 'nowrap',
            }}>
              {tab === 'visits'   ? `🏥 History (${totalHistoryCount})`
               : tab === 'invoices' ? `🧾 Invoices (${invoices.length})`
               : tab === 'details'  ? '👤 Details'
               : '📷 Photos'}
            </button>
          ))}
        </div>

        {/* ── FIX 4: Details tab — view + inline edit ── */}
        {activeTab === 'details' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
              {editingDetails ? (
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => setEditingDetails(false)} style={{
                    padding: '5px 12px', borderRadius: 6, border: `1px solid ${T.gray200}`,
                    background: T.white, color: T.gray600, fontSize: 12,
                    cursor: 'pointer', fontFamily: T.font,
                  }}>Cancel</button>
                  <button onClick={saveDetails} disabled={savingDetails} style={{
                    padding: '5px 14px', borderRadius: 6, border: 'none',
                    background: savingDetails ? T.teal400 : T.teal600, color: T.white,
                    fontSize: 12, fontWeight: 700,
                    cursor: savingDetails ? 'not-allowed' : 'pointer', fontFamily: T.font,
                  }}>{savingDetails ? 'Saving…' : '💾 Save'}</button>
                </div>
              ) : (
                <button onClick={startEditDetails} style={{
                  padding: '5px 12px', borderRadius: 6, border: `1.5px solid ${T.teal500}`,
                  background: T.teal50, color: T.teal700, fontSize: 12, fontWeight: 600,
                  cursor: 'pointer', fontFamily: T.font,
                }}>✏️ Edit Details</button>
              )}
            </div>

            {editingDetails ? (
              <div>
                <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: T.gray500, display: 'block', marginBottom: 3 }}>Phone</label>
                    <input style={inp2} value={detailEdits.phone || ''} onChange={e => setDetailEdits(d => ({ ...d, phone: e.target.value }))} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: T.gray500, display: 'block', marginBottom: 3 }}>Age</label>
                    <input type="number" style={inp2} value={detailEdits.age ?? ''} onChange={e => setDetailEdits(d => ({ ...d, age: e.target.value }))} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: T.gray500, display: 'block', marginBottom: 3 }}>Gender</label>
                    <select style={inp2} value={detailEdits.gender || ''} onChange={e => setDetailEdits(d => ({ ...d, gender: e.target.value }))}>
                      <option value="">—</option>
                      {GENDERS.map(g => <option key={g}>{g}</option>)}
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: T.gray500, display: 'block', marginBottom: 3 }}>Blood Group</label>
                    <select style={inp2} value={detailEdits.blood_group || ''} onChange={e => setDetailEdits(d => ({ ...d, blood_group: e.target.value }))}>
                      <option value="">—</option>
                      {BLOOD_GROUPS.map(b => <option key={b}>{b}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: T.gray500, display: 'block', marginBottom: 3 }}>Skin Type</label>
                  <select style={inp2} value={detailEdits.skin_type || ''} onChange={e => setDetailEdits(d => ({ ...d, skin_type: e.target.value }))}>
                    <option value="">—</option>
                    {SKIN_TYPES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: T.gray500, display: 'block', marginBottom: 3 }}>Email</label>
                  <input type="email" style={inp2} value={detailEdits.email || ''} onChange={e => setDetailEdits(d => ({ ...d, email: e.target.value }))} />
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: T.gray500, display: 'block', marginBottom: 3 }}>Allergies</label>
                  <input style={inp2} value={detailEdits.allergies || ''} onChange={e => setDetailEdits(d => ({ ...d, allergies: e.target.value }))} placeholder="e.g. Penicillin, dust mites…" />
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: T.gray500, display: 'block', marginBottom: 3 }}>Date of Birth</label>
                  <input type="date" style={inp2} value={detailEdits.date_of_birth || ''} onChange={e => setDetailEdits(d => ({ ...d, date_of_birth: e.target.value }))} />
                </div>
              </div>
            ) : (
              <div>
                {[
                  ['Phone',       patient.phone],
                  ['Email',       patient.email],
                  ['Age',         patient.age ? `${patient.age} yrs` : null],
                  ['Gender',      patient.gender],
                  ['DOB',         patient.date_of_birth ? new Date(patient.date_of_birth + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : null],
                  ['Skin Type',   patient.skin_type],
                  ['Blood Group', patient.blood_group],
                  ['Allergies',   patient.allergies],
                  ['Address',     patient.address],
                  ['Registered',  fmtDate(patient.created_at)],
                ].filter(([, v]) => v).map(([label, value]) => (
                  <div key={label} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: `1px solid ${T.gray100}` }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: T.gray400, width: 100, flexShrink: 0 }}>{label}</span>
                    <span style={{ fontSize: 13, color: T.gray700 }}>{value}</span>
                  </div>
                ))}
                {patient.notes && (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 11.5, fontWeight: 700, color: T.gray500, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
                      Chief Complaint / Notes
                    </div>
                    <div style={{ background: T.amber50, border: `1px solid #fde68a`, borderRadius: 8, padding: '10px 12px', fontSize: 13, color: T.gray700, lineHeight: 1.5 }}>
                      {patient.notes}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* FIX 5: Photos tab — already working */}
        {activeTab === 'photos' && (
          <PhotosTab patientId={patient.id} patientName={patient.full_name} />
        )}

        {/* ── FIX 2: Invoices tab with Generate Invoice button ── */}
        {activeTab === 'invoices' && (
          <div>
            {role === 'receptionist' && (
              <div style={{ fontSize: 11.5, color: T.gray400, marginBottom: 8 }}>Showing today's invoices and pending balances.</div>
            )}
            {can('invoices.create') && <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
              <button onClick={() => setInvModalOpen(true)} style={{
                padding: '6px 14px', borderRadius: 7, border: 'none',
                background: T.teal600, color: T.white,
                fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: T.font,
              }}>+ Generate Invoice</button>
            </div>}
            {invoices.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>🧾</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.gray600 }}>No invoices yet</div>
                {can('invoices.create') && <div style={{ fontSize: 12, color: T.gray400, marginTop: 4 }}>Generate an invoice using the button above</div>}
              </div>
            ) : (
              <div style={{ borderRadius: 8, overflow: 'hidden', border: `1px solid ${T.gray200}` }}>
                {invoices.map((inv, i) => {
                  const st = normStatus(inv)
                  const stBg = st === 'paid' ? '#dcfce7' : st === 'partial' ? '#fef3c7' : '#fee2e2'
                  const stCl = st === 'paid' ? '#16a34a' : st === 'partial' ? '#d97706' : T.red600
                  return (
                    <div key={inv.id} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 14px',
                      borderBottom: i < invoices.length - 1 ? `1px solid ${T.gray100}` : 'none',
                      background: i % 2 === 0 ? T.white : T.gray50,
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: T.teal700 }}>
                          {inv.invoice_number || inv.id?.slice(0, 8)}
                        </div>
                        <div style={{ fontSize: 11, color: T.gray400, marginTop: 1 }}>
                          {fmtInvDate(invDate(inv))}
                        </div>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: T.gray900 }}>
                        {fmtINR(inv.total)}
                      </div>
                      <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', background: stBg, color: stCl }}>
                        {st}
                      </span>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {/* ── FIX 1: History/Visits tab — appointments + prescriptions + notes ── */}
        {activeTab === 'visits' && (
          visitsLoading ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: T.gray400, fontSize: 13 }}>
              Loading visit history…
            </div>
          ) : (
            <div>
              {/* Inline add-note form */}
              {addNoteOpen && (
                <div style={{
                  background: T.blue50, border: `1.5px solid #bfdbfe`,
                  borderRadius: 10, padding: 14, marginBottom: 14,
                }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: T.blue600, marginBottom: 8 }}>📝 Add Visit Note</div>
                  <textarea
                    rows={3} value={newNote} onChange={e => setNewNote(e.target.value)}
                    placeholder="Enter note, prescription details, instructions…"
                    style={{
                      width: '100%', padding: '8px 10px', borderRadius: 7,
                      border: `1px solid #bfdbfe`, fontSize: 12.5, outline: 'none',
                      resize: 'vertical', fontFamily: T.font, boxSizing: 'border-box',
                      background: T.white,
                    }}
                  />
                  <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                    <button onClick={() => { setAddNoteOpen(false); setNewNote('') }} style={{
                      flex: 1, padding: '6px', borderRadius: 6, border: `1px solid ${T.gray200}`,
                      background: T.white, color: T.gray600, fontSize: 12,
                      cursor: 'pointer', fontFamily: T.font,
                    }}>Cancel</button>
                    <button onClick={saveNote} disabled={savingNote || !newNote.trim()} style={{
                      flex: 2, padding: '6px', borderRadius: 6, border: 'none',
                      background: savingNote || !newNote.trim() ? '#93c5fd' : T.blue600,
                      color: T.white, fontSize: 12, fontWeight: 700,
                      cursor: savingNote || !newNote.trim() ? 'not-allowed' : 'pointer', fontFamily: T.font,
                    }}>{savingNote ? 'Saving…' : '💾 Save Note'}</button>
                  </div>
                </div>
              )}

              {/* Empty state */}
              {totalHistoryCount === 0 && !addNoteOpen ? (
                <div style={{ textAlign: 'center', padding: '32px 0' }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.gray600 }}>No visit history yet</div>
                  <div style={{ fontSize: 12, color: T.gray400, marginTop: 4 }}>Book an appointment or add a note to begin</div>
                </div>
              ) : (
                <div style={{ position: 'relative' }}>
                  <div style={{ position: 'absolute', left: 14, top: 8, bottom: 8, width: 2, background: T.teal100 }} />
                  {(() => {
                    const apptItems = visits.map(v      => ({ _type: 'appt', _sort: v.date + 'T' + (v.time || '00:00'), ...v }))
                    const noteItems = visitNotes.map(n  => ({ _type: 'note', _sort: n.created_at || '', ...n }))
                    const rxItems   = prescriptions.map(rx => ({ _type: 'rx',   _sort: rx.created_at || '', ...rx }))
                    const timeline  = [...apptItems, ...noteItems, ...rxItems].sort((a, b) => b._sort.localeCompare(a._sort))
                    return timeline.map(item => {
                      const key = item._type + item.id

                      // ── Appointment card ──
                      if (item._type === 'appt') {
                        return (
                          <div key={key} style={{ display: 'flex', gap: 14, marginBottom: 10, position: 'relative' }}>
                            <div style={{
                              width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                              background: item.status === 'Completed' ? T.teal600 : T.gray200,
                              border: `2px solid ${item.status === 'Completed' ? T.teal400 : T.gray300}`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 13, color: T.white, fontWeight: 700, zIndex: 1,
                            }}>
                              {item.status === 'Completed' ? '✓' : item.status === 'Cancelled' ? '✗' : '○'}
                            </div>
                            <div style={{ flex: 1, background: T.gray50, borderRadius: 9, padding: '9px 12px', border: `1px solid ${T.gray100}` }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 3 }}>
                                <span style={{ fontSize: 12.5, fontWeight: 700, color: T.gray900 }}>
                                  {item.service_type || item.appointment_type || 'Appointment'}
                                </span>
                                <Badge text={item.status} colors={STATUS_COLORS[item.status]} />
                              </div>
                              <div style={{ fontSize: 11.5, color: T.gray500 }}>
                                {fmtDate(item.date)}{item.time ? ` · ${fmtTime(item.time)}` : ''}
                              </div>
                              {item.doctor && <div style={{ fontSize: 11.5, color: T.teal600, marginTop: 2 }}>{item.doctor}</div>}
                            </div>
                          </div>
                        )
                      }

                      // ── Prescription card (from prescriptions table) ──
                      if (item._type === 'rx') {
                        const rxDate = item.created_at
                          ? new Date(item.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                          : '—'
                        let meds = []
                        try { meds = Array.isArray(item.medicines) ? item.medicines : JSON.parse(item.medicines || '[]') } catch {}
                        const isOpen = expandedItem === key
                        return (
                          <div key={key} style={{ padding: '0 0 10px 0', position: 'relative' }}>
                            <div style={{ position: 'absolute', left: 9, top: 10, width: 11, height: 11, borderRadius: '50%', background: '#7c3aed', border: `2px solid ${T.white}`, zIndex: 1 }} />
                            <div style={{ marginLeft: 24, border: `1.5px solid #c4b5fd`, borderRadius: 8, background: '#f5f3ff', overflow: 'hidden' }}>
                              <div onClick={() => setExpanded(isOpen ? null : key)}
                                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', cursor: 'pointer', userSelect: 'none' }}>
                                <span style={{ fontSize: 13 }}>💊</span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 12, fontWeight: 700, color: '#6d28d9' }}>
                                    Prescription{item.diagnosis ? ` — ${item.diagnosis}` : ''}
                                  </div>
                                  <div style={{ fontSize: 11, color: T.gray500, marginTop: 1 }}>
                                    {rxDate}
                                    {item.doctor_name ? ` · Dr. ${item.doctor_name}` : ''}
                                    {meds.length > 0 ? ` · ${meds.length} med${meds.length !== 1 ? 's' : ''}` : ''}
                                  </div>
                                </div>
                                <span style={{ fontSize: 10, color: T.gray400, transform: isOpen ? 'rotate(90deg)' : 'none', display: 'inline-block', transition: 'transform .2s' }}>▶</span>
                              </div>
                              {isOpen && (
                                <div style={{ borderTop: `1px solid #c4b5fd`, padding: '8px 12px 10px' }}>
                                  {item.chief_complaint && (
                                    <div style={{ fontSize: 12, color: T.gray600, marginBottom: 6 }}>
                                      <b>Complaint:</b> {item.chief_complaint}
                                    </div>
                                  )}
                                  {meds.length > 0 && meds.map((m, mi) => (
                                    <div key={mi} style={{
                                      fontSize: 12, color: T.gray700, padding: '3px 0',
                                      borderBottom: mi < meds.length - 1 ? `1px solid #ede9fe` : 'none',
                                    }}>
                                      <b>{m.name}</b>{m.form ? ` (${m.form})` : ''} — {m.dose}{m.dur ? `, ${m.dur}` : ''}
                                    </div>
                                  ))}
                                  {item.advice && (
                                    <div style={{ fontSize: 11.5, color: T.gray500, marginTop: 6 }}>
                                      <b>Advice:</b> {Array.isArray(item.advice) ? item.advice.join(', ') : item.advice}
                                    </div>
                                  )}
                                  <button onClick={e => { e.stopPropagation(); openRxPadWithData(item) }} style={{
                                    marginTop: 8, padding: '5px 10px', borderRadius: 6, border: `1px solid #c4b5fd`,
                                    background: '#ede9fe', color: '#6d28d9', fontSize: 11.5, fontWeight: 600,
                                    cursor: 'pointer', fontFamily: T.font,
                                  }}>✏️ Open in Rx Pad</button>
                                </div>
                              )}
                            </div>
                          </div>
                        )
                      }

                      // ── Visit note card ──
                      const noteType  = item.note_type || 'Note'
                      const noteColor = noteType === 'Prescription'
                        ? { dot: '#7c3aed', bg: '#f5f3ff', color: '#6d28d9', border: '#c4b5fd' }
                        : noteType === 'Lab Result'
                        ? { dot: '#0369a1', bg: '#e0f2fe', color: '#0369a1', border: '#7dd3fc' }
                        : { dot: T.teal600, bg: T.teal50, color: T.teal700, border: T.teal200 }
                      const noteIcon = noteType === 'Prescription' ? '📋' : noteType === 'Lab Result' ? '🔬' : '📝'
                      const noteDate = item.created_at
                        ? new Date(item.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                        : '—'
                      const isOpen   = expandedItem === key
                      return (
                        <div key={key} style={{ padding: '0 0 10px 0', position: 'relative' }}>
                          <div style={{ position: 'absolute', left: 9, top: 10, width: 11, height: 11, borderRadius: '50%', background: noteColor.dot, border: `2px solid ${T.white}`, zIndex: 1 }} />
                          <div style={{ marginLeft: 24, border: `1.5px solid ${noteColor.border}`, borderRadius: 8, background: noteColor.bg, overflow: 'hidden' }}>
                            <div onClick={() => setExpanded(isOpen ? null : key)}
                              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', cursor: 'pointer', userSelect: 'none' }}>
                              <span style={{ fontSize: 13 }}>{noteIcon}</span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: noteColor.color }}>{noteType}</div>
                                <div style={{ fontSize: 11, color: T.gray500, marginTop: 1 }}>
                                  {noteDate}{item.created_by ? ' · Dr. ' + item.created_by : ''}
                                </div>
                              </div>
                              <span style={{ fontSize: 10, color: T.gray400, transform: isOpen ? 'rotate(90deg)' : 'none', display: 'inline-block', transition: 'transform .2s' }}>▶</span>
                            </div>
                            {isOpen && (
                              <div style={{ borderTop: `1px solid ${noteColor.border}`, padding: '8px 12px 10px' }}>
                                {item.note_text && (
                                  <pre style={{ fontSize: 12, color: T.gray700, lineHeight: 1.6, whiteSpace: 'pre-wrap', margin: 0, fontFamily: T.font }}>{item.note_text}</pre>
                                )}
                                {noteType === 'Prescription' && can('prescriptions.write') && (
                                  <button onClick={e => { e.stopPropagation(); openRxPadWithData(item) }} style={{
                                    marginTop: 8, padding: '5px 10px', borderRadius: 6, border: `1px solid #c4b5fd`,
                                    background: '#ede9fe', color: '#6d28d9', fontSize: 11.5, fontWeight: 600,
                                    cursor: 'pointer', fontFamily: T.font,
                                  }}>✏️ Open in Rx Pad</button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })
                  })()}
                </div>
              )}
            </div>
          )
        )}
      </div>

      {/* Delete — admin only */}
      {isAdmin && <div style={{ padding: '0 16px 20px', marginTop: 'auto' }}>
        {!confirmDelete ? (
          <button onClick={() => setConfirmDel(true)} style={{
            width: '100%', padding: '8px', borderRadius: 8,
            border: `1.5px solid #fecdd3`, background: T.red50,
            color: T.red600, fontSize: 12.5, fontWeight: 600,
            cursor: 'pointer', fontFamily: T.font,
          }}>
            🗑 Delete Patient Record
          </button>
        ) : (
          <div style={{
            background: T.red50, border: '1px solid #fecdd3',
            borderRadius: 10, padding: '14px', textAlign: 'center',
          }}>
            <p style={{ margin: '0 0 10px', fontSize: 13, color: T.red600, fontWeight: 600 }}>
              Permanently delete this patient and all their records?
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setConfirmDel(false)} style={{
                flex: 1, padding: '8px', borderRadius: 7, border: `1px solid ${T.gray200}`,
                background: T.white, color: T.gray600, fontSize: 12.5, fontWeight: 600,
                cursor: 'pointer', fontFamily: T.font,
              }}>Cancel</button>
              <button onClick={handleDelete} disabled={deleting} style={{
                flex: 1, padding: '8px', borderRadius: 7, border: 'none',
                background: T.red600, color: T.white, fontSize: 12.5, fontWeight: 700,
                cursor: deleting ? 'not-allowed' : 'pointer', fontFamily: T.font,
              }}>
                {deleting ? 'Deleting…' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        )}
      </div>}

      {/* ══ Book Next Appointment Modal ══ */}
      {bookNextOpen && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(8,40,40,0.55)',
          zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20, fontFamily: T.font,
        }} onClick={e => { if (e.target === e.currentTarget) setBookNextOpen(false) }}>
          <div style={{ background: T.white, borderRadius: 14, width: '100%', maxWidth: 380, padding: 24, boxShadow: '0 16px 48px rgba(0,0,0,0.3)' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: T.teal900 }}>📅 Book Next Appointment</h3>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: T.gray500, display: 'block', marginBottom: 3 }}>Date *</label>
              <input type="date" style={inp2} value={bookForm.date} onChange={e => setBookForm(f => ({ ...f, date: e.target.value }))} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: T.gray500, display: 'block', marginBottom: 3 }}>Time</label>
              <input type="time" style={inp2} value={bookForm.time} onChange={e => setBookForm(f => ({ ...f, time: e.target.value }))} />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: T.gray500, display: 'block', marginBottom: 3 }}>Service / Reason</label>
              <input style={inp2} value={bookForm.service} onChange={e => setBookForm(f => ({ ...f, service: e.target.value }))} placeholder="e.g. Follow-up, Chemical Peel…" />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setBookNextOpen(false)} style={{
                flex: 1, padding: '9px', borderRadius: 7, border: `1px solid ${T.gray200}`,
                background: T.white, color: T.gray600, fontSize: 13, cursor: 'pointer', fontFamily: T.font,
              }}>Cancel</button>
              <button onClick={saveBookNext} disabled={bookingSaving || !bookForm.date} style={{
                flex: 2, padding: '9px', borderRadius: 7, border: 'none',
                background: bookingSaving || !bookForm.date ? T.teal400 : T.teal600,
                color: T.white, fontSize: 13, fontWeight: 700,
                cursor: bookingSaving || !bookForm.date ? 'not-allowed' : 'pointer', fontFamily: T.font,
              }}>
                {bookingSaving ? 'Booking…' : '✅ Book Appointment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ Full Invoice Modal ══ */}
      {invModalOpen && (
        <InvoiceModal
          patient={patient}
          existingInvoices={invoices}
          onClose={() => setInvModalOpen(false)}
          onSaved={inv => {
            setInvoices(v => [inv, ...v])
            setActiveTab('invoices')
          }}
        />
      )}

      {/* ══ Full Rx Pad Modal ══ */}
      {rxModal !== null && (
        <PatientRxModal
          patient={patient}
          prefill={rxModal}
          onClose={() => setRxModal(null)}
          onSaved={note => {
            if (note) setVisitNotes(n => [note, ...n])
            // Reload prescriptions so the new structured row appears in history
            // and future "Open in Rx Pad" clicks work immediately
            supabase.from('prescriptions').select('*').eq('patient_id', patient.id)
              .order('created_at', { ascending: false })
              .then(({ data }) => { if (data) setPrescriptions(data) })
            setRxModal(null)
          }}
        />
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PATIENTS MODULE
// ═══════════════════════════════════════════════════════════════════════════════
export default function Patients() {
  const { can } = useAuth()
  const [patients, setPatients]       = useState([])
  const [loading, setLoading]         = useState(true)
  const [error, setError]             = useState('')
  const [search, setSearch]           = useState('')
  const [skinFilter, setSkinFilter]   = useState('')
  const [selected, setSelected]       = useState(null)
  const [modal, setModal]             = useState(null) // null | 'add' | patient object
  const searchRef = useRef(null)

  const load = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const { data, error } = await supabase
        .from('patients')
        .select('*')
        .order('created_at', { ascending: false })
      if (error) throw error
      setPatients(data || [])
    } catch (e) {
      setError(friendlyError(e, 'Failed to load patients'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  // Count visits per patient
  const [visitCounts, setVisitCounts] = useState({})
  useEffect(() => {
    supabase
      .from('appointments')
      .select('patient_id')
      .then(({ data }) => {
        const counts = {}
        ;(data || []).forEach(r => { counts[r.patient_id] = (counts[r.patient_id] || 0) + 1 })
        setVisitCounts(counts)
      })
  }, [patients])

  const filtered = patients.filter(p => {
    const q = search.toLowerCase()
    const matchSearch = !q || (p.full_name || '').toLowerCase().includes(q) || (p.phone || '').includes(q)
    const matchSkin   = !skinFilter || p.skin_type === skinFilter
    return matchSearch && matchSkin
  })

  function handleSaved(saved) {
    setPatients(prev => {
      const idx = prev.findIndex(p => p.id === saved.id)
      if (idx >= 0) { const next = [...prev]; next[idx] = saved; return next }
      return [saved, ...prev]
    })
    setModal(null)
    setSelected(saved)
  }
  function handleDeleted(id) {
    setPatients(prev => prev.filter(p => p.id !== id))
    setSelected(null)
  }

  return (
    <div style={{
      display: 'flex', height: '100%', overflow: 'hidden',
      fontFamily: T.font, background: T.gray50,
    }}>

      {/* ── Left: patient list ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Toolbar */}
        <div style={{
          padding: '18px 24px', background: T.white,
          borderBottom: `1px solid ${T.gray200}`,
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: T.gray900 }}>Patients</h2>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: T.gray400 }}>
              {loading ? 'Loading…' : `${filtered.length} of ${patients.length} patient${patients.length !== 1 ? 's' : ''}`}
            </p>
          </div>

          {/* Search */}
          <div style={{ position: 'relative', width: 240 }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: T.gray400 }}>🔍</span>
            <input
              ref={searchRef}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name or phone…"
              style={{
                width: '100%', padding: '8px 12px 8px 32px',
                border: `1.5px solid ${T.gray200}`, borderRadius: 8,
                fontSize: 13, color: T.gray900, background: T.gray50,
                outline: 'none', fontFamily: T.font, boxSizing: 'border-box',
              }}
              onFocus={e => { e.target.style.borderColor = T.teal500; e.target.style.boxShadow = `0 0 0 3px ${T.teal100}` }}
              onBlur={e =>  { e.target.style.borderColor = T.gray200;  e.target.style.boxShadow = 'none' }}
            />
          </div>

          {/* Skin type filter */}
          <select
            value={skinFilter} onChange={e => setSkinFilter(e.target.value)}
            style={{
              padding: '8px 12px', border: `1.5px solid ${T.gray200}`, borderRadius: 8,
              fontSize: 13, color: skinFilter ? T.gray900 : T.gray400,
              background: T.white, cursor: 'pointer', fontFamily: T.font, outline: 'none',
            }}
          >
            <option value="">All Skin Types</option>
            {SKIN_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>

          {/* Add button — admin & reception only */}
          {can('patients.add') && <button onClick={() => setModal('add')} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '9px 18px', borderRadius: 8, border: 'none',
            background: T.teal600, color: T.white,
            fontSize: 13, fontWeight: 700, cursor: 'pointer',
            fontFamily: T.font, boxShadow: '0 2px 8px rgba(20,128,128,0.3)',
            whiteSpace: 'nowrap',
          }}>
            + Add Patient
          </button>}
        </div>

        {/* Error */}
        {error && (
          <div style={{
            margin: '12px 24px 0', padding: '10px 14px', borderRadius: 8,
            background: T.red50, border: '1px solid #fecdd3', color: T.red600, fontSize: 13,
          }}>⚠️ {error}</div>
        )}

        {/* Table */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: T.gray400, fontSize: 14 }}>
              Loading patients…
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '80px 20px' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>{search || skinFilter ? '🔍' : '👤'}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: T.gray600, marginBottom: 6 }}>
                {search || skinFilter ? 'No patients match your search' : 'No patients yet'}
              </div>
              <div style={{ fontSize: 13, color: T.gray400, marginBottom: 20 }}>
                {search || skinFilter ? 'Try a different name, phone or skin type' : can('patients.add') ? 'Click "+ Add Patient" to register your first patient' : 'Patients registered at reception will appear here'}
              </div>
              {!search && !skinFilter && can('patients.add') && (
                <button onClick={() => setModal('add')} style={{
                  padding: '10px 22px', borderRadius: 8, border: 'none',
                  background: T.teal600, color: T.white, fontSize: 13, fontWeight: 700,
                  cursor: 'pointer', fontFamily: T.font,
                }}>+ Add First Patient</button>
              )}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 5 }}>
                <tr style={{ background: T.gray100 }}>
                  {['Patient', 'Age / Gender', 'Phone', 'Skin Type', 'Blood Group', 'First Visit', 'Total Visits', ''].map(h => (
                    <th key={h} style={{
                      padding: '10px 16px', textAlign: 'left',
                      fontSize: 11, fontWeight: 700, color: T.gray500,
                      letterSpacing: '0.5px', textTransform: 'uppercase',
                      borderBottom: `2px solid ${T.gray200}`,
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((p, i) => {
                  const isSelected = selected?.id === p.id
                  const [bg] = avatarColor(p.full_name)
                  return (
                    <tr
                      key={p.id}
                      onClick={() => setSelected(isSelected ? null : p)}
                      style={{
                        background: isSelected ? T.teal50 : i % 2 === 0 ? T.white : T.gray50,
                        borderBottom: `1px solid ${T.gray100}`,
                        cursor: 'pointer', transition: 'background 0.1s',
                        borderLeft: isSelected ? `3px solid ${T.teal500}` : '3px solid transparent',
                      }}
                      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = T.teal50 + '88' }}
                      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = i % 2 === 0 ? T.white : T.gray50 }}
                    >
                      {/* Patient name + avatar */}
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{
                            width: 34, height: 34, borderRadius: '50%',
                            background: `linear-gradient(135deg, ${bg}, ${bg}aa)`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 12, fontWeight: 800, color: T.white, flexShrink: 0,
                          }}>
                            {initials(p.full_name)}
                          </div>
                          <span style={{ fontWeight: 600, color: T.gray900 }}>{p.full_name}</span>
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px', color: T.gray600 }}>
                        {[p.age && `${p.age} yrs`, p.gender].filter(Boolean).join(' · ') || '—'}
                      </td>
                      <td style={{ padding: '12px 16px', color: T.gray600 }}>{p.phone}</td>
                      <td style={{ padding: '12px 16px' }}>
                        {p.skin_type
                          ? <Badge text={p.skin_type} colors={{ bg: T.teal50, color: T.teal700, border: T.teal100 }} />
                          : <span style={{ color: T.gray300 }}>—</span>}
                      </td>
                      <td style={{ padding: '12px 16px', color: T.gray600 }}>{p.blood_group || '—'}</td>
                      <td style={{ padding: '12px 16px', color: T.gray500, whiteSpace: 'nowrap' }}>{fmtDate(p.created_at)}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <span style={{
                          background: T.teal50, color: T.teal700, border: `1px solid ${T.teal100}`,
                          borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 700,
                        }}>{visitCounts[p.id] || 0}</span>
                      </td>
                      <td style={{ padding: '12px 12px' }}>
                        <button
                          onClick={e => { e.stopPropagation(); setModal(p) }}
                          style={{
                            padding: '5px 10px', borderRadius: 6,
                            border: `1px solid ${T.gray200}`, background: T.white,
                            color: T.gray500, fontSize: 12, cursor: 'pointer', fontFamily: T.font,
                          }}
                        >✏️</button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Right: profile panel ── */}
      {selected && (
        <ProfilePanel
          patient={selected}
          onClose={() => setSelected(null)}
          onEdit={p => setModal(p)}
          onDeleted={handleDeleted}
          onPatientUpdated={p => {
            setPatients(prev => prev.map(x => x.id === p.id ? p : x))
            setSelected(p)
          }}
        />
      )}

      {/* ── Modal ── */}
      {modal && (
        <PatientModal
          patient={modal === 'add' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  )
}
